// ANGULAR
import { Router } from '@angular/router';
import { FormControl, FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { Title } from '@angular/platform-browser';
import { HttpClient } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
// SERVICES
import { AlertService } from '@services/alert.service';
import { GlobalService } from '@services/global.service';
import { AuthService } from '@api-requests-services/auth.service';
// MODELS
import { APIUrl, EnvironmentsAPIUrl } from '@models/api-url.model';
import { PostLoginRequest, TotpRegisterAuth } from '@models/auth.model';
// STORE
import { Store } from '@ngrx/store';
import { MainState } from '@store/main.reducer';
import { login, logoutSuccess, recoverPassword, verifyTotp } from '@store/session/session.actions';
import { selectError, selectLoginInProcess, selectVerifyTotpInProcess } from '@store/app/app.selectors';
import { updateReturnHome, updateLoginInProcess, updateVerifyTotpInProcess } from '@store/app/app.actions';
import { selectAuthMethods, selectTotpRegisterAuth, selectVerifyMethodError } from '@store/session/session.selectors';
// ENVIRONMENT
import { environment } from 'src/environments/environment';
// RXJS
import { Observable, Subscription } from 'rxjs';
// OTHERS
import { QRCodeComponent } from 'angularx-qrcode';
import { Paths } from '@constants/paths.const';
import { userEmailL } from '@constants/e.const';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrl: './login.component.css',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    QRCodeComponent
  ]
})
export class LoginComponent implements OnInit, OnDestroy {
  private errorSubscription: Subscription = new Subscription();
  private loginInProcessSubscription: Subscription = new Subscription();
  private verifyTotpInProcessSubscription: Subscription = new Subscription();
  loginForm: UntypedFormGroup;
  loginInProcess: boolean = false;
  errorMessage: string;
  wrongLogin: boolean = false;
  insertTotp: boolean = false;
  totpRegisterAuth: TotpRegisterAuth | null = null;
  totpCode: string = "";
  verifyTotpInProcess: boolean = false;
  verifyMethodError: string = "";
  attempts: number = 0;
  // Variables de los ajustes de desarrollador
  clicks: number = 0;
  clickTimeout: any;
  devSettings: boolean = false;
  wrongPasswordDevSettings: boolean = false;
  logDevSettings: boolean = false;
  selectedApiUrl: APIUrl;
  passwordDevSettings: string;
  environmentEtrasaApiUrl: APIUrl = EnvironmentsAPIUrl;
  apiUrls: APIUrl[] = [];

  constructor(
    private alertService: AlertService,
    private globalService: GlobalService,
    private authService: AuthService,
    private title: Title,
    private router: Router,
    private fb: UntypedFormBuilder,
    private store: Store<MainState>,
    private http: HttpClient
  ) {
    this.title.setTitle('Oráculo - Login');
  }

  async ngOnInit() {
    this.errorSubscription = this.store.select(selectError).subscribe(
      error => {
        // console.log(error);
        if (error && error.url.includes('/Auth/UserLogin')) {
          this.errorMessage = JSON.parse(error.error);
          this.wrongLogin = true;
        } else {
          this.wrongLogin = false;
        }
      }
    );

    this.environmentEtrasaApiUrl = await this.globalService.loadAPIUrl();
    this.selectedApiUrl = this.environmentEtrasaApiUrl;

    this.store.dispatch(updateReturnHome({ returnHome: false }));

    this.createForm();

    if (!environment.production) {
      this.getDataUrls().subscribe(
        (data) => {
          // console.log('Urls de APIs: ', data);
          this.apiUrls = data;
        },
        (error) => {
          this.apiUrls = [];
          console.error('Error loading "api-urls.json" file', error);
        }
      );
    }

    this.store.select(selectAuthMethods).subscribe(
      authMethods => {
        this.insertTotp = !!authMethods;
        this.totpCode = '';        
      }
    );

    this.store.select(selectTotpRegisterAuth).subscribe(totpRegisterAuth => this.totpRegisterAuth = !!totpRegisterAuth ? totpRegisterAuth : null );

    this.store.select(selectVerifyMethodError).subscribe(verifyMethodError => this.verifyMethodError = !!verifyMethodError ? verifyMethodError.replaceAll('"', '') : "");
  }

  createForm(): void {
    this.loginForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', Validators.required],
    });

    if (typeof window !== 'undefined') { (this.loginForm.get('email') as FormControl).setValue(localStorage.getItem(userEmailL) ?? ''); }

    this.loginInProcessSubscription = this.store.select(selectLoginInProcess).subscribe(
      value => {
        this.loginInProcess = value;

        if (value) {
          (this.loginForm.get('email') as FormControl).disable();
          (this.loginForm.get('password') as FormControl).disable();
        } else {
          (this.loginForm.get('email') as FormControl).enable();
          (this.loginForm.get('password') as FormControl).enable();
        }
      }
    );

    this.verifyTotpInProcessSubscription = this.store.select(selectVerifyTotpInProcess).subscribe(value => this.verifyTotpInProcess = value );
  }

  get loginControls() { return this.loginForm.controls; }

  getDataUrls(): Observable<any> {
    return this.http.get('/assets/data/api-urls.json');
  }

  // showPassword(): void {
  //   const input = document.getElementById('input-password') as HTMLInputElement;

  //   if (input.type === "password") {
  //     input.type = "text";
  //   } else {
  //     input.type = "password";
  //   }
  // }

  showPassword(): void {
    const input = document.getElementById('input-password') as HTMLInputElement | null;

    if (!!input) {
      if (input.type === "password") {
        input.type = "text";
      } else {
        input.type = "password";
      }
    }
  }

  m2mLogin(): void {
    this.authService.postM2MLogin().subscribe(
      response => {
        if (response) { this.router.navigate([Paths.home]); }
      },
      (error) => {
        this.alertService.swalError(error.error);
        console.error('Error Login:', error);
      }
    );
  }

  login(): void {
    this.store.dispatch(updateLoginInProcess({ loginInProcess: true }));
    this.wrongLogin = false;
    const postLoginRequest: PostLoginRequest = {
      email: (this.loginForm.get('email') as FormControl).value,
      passwordUsuario: (this.loginForm.get('password') as FormControl).value
    };
    if (typeof window !== 'undefined') { localStorage.setItem(userEmailL, (this.loginForm.get('email') as FormControl).value); }
    this.store.dispatch(login({ postLoginRequest: postLoginRequest }));
    this.loginForm.get('password')?.setValue('');
    // console.log('CREDENCIALES AL LOGEAR', postLoginRequest);
  }

  copyManualKey(): void {
    const key: string | null = this.totpRegisterAuth?.manualEntryKey ?? null;

    if (!key) return;

    navigator.clipboard.writeText(key);
  }

  verifyMethod(): void {
    this.store.dispatch(updateVerifyTotpInProcess({ verifyTotpInProcess: true }));
    this.store.dispatch(verifyTotp({ code: this.totpCode.trim() }));
    this.totpCode = "";
    this.verifyMethodError = "";
  }

  recoverPassword(): void {
    if ((this.loginForm.get('email') as FormControl).value) {
      this.store.dispatch(recoverPassword({ email: (this.loginForm.get('email') as FormControl).value }));
      this.wrongLogin = false;
    } else {
      this.errorMessage = 'Debe escribir un correo para solicitar una nueva contraseña.';
      this.wrongLogin = true;
    }
    return;
  }

  ngOnDestroy(): void {
    this.errorSubscription.unsubscribe();
    this.loginInProcessSubscription.unsubscribe();
    this.verifyTotpInProcessSubscription.unsubscribe();
  }

  openDevSettings(): void {
    this.clicks = this.clicks + 1;

    if (this.clicks === 5 && !environment.production) {
      this.devSettings = true;
      this.clicks = 0;
    } else if (this.clicks === 1) {
      this.clickTimeout = setTimeout(() => {
        this.clicks = 0;
      }, 3000);
    }
  }

  changeLogDevSettings(): void {
    if (this.passwordDevSettings === 'dev24') {
      this.logDevSettings = true;
      this.wrongPasswordDevSettings = false;
      this.passwordDevSettings = '';
    } else {
      this.logDevSettings = false;
      this.wrongPasswordDevSettings = true;
    }
  }

  selectApiUrl(): void {
    this.globalService.setApiUrl(this.selectedApiUrl);
    this.goToLogin();

    this.alertService.showBasicMessage(
      `Url de API actual`,
      `${this.globalService.apiUrl.name} - ${this.globalService.apiUrl.url}`,
      "success",
      true
    );
  }

  goToLogin(): void {
    this.devSettings = false;
    this.logDevSettings = false;
    this.store.dispatch(logoutSuccess());
  }
}